import useImageSource from '../../hooks/useImageSource';

import type { Asset, ImageConfig } from '@tectonic/types';
import type { ComponentProps, FC } from 'react';

interface ImageAssetProps extends Omit<ComponentProps<'img'>, 'alt' | 'src'> {
  asset?: Asset;
  config?: ImageConfig;
}

const ImageAsset: FC<ImageAssetProps> = ({
  asset,
  config,
  style,
  className,
  ...otherProps
}) => {
  const sources = useImageSource(asset, config?.assetScale);

  if (!asset) return null;

  const derivedStyles = {
    width: config?.width ?? style?.width,
    height: config?.height ?? style?.height,
    aspectRatio:
      config?.aspectRatio ||
      style?.aspectRatio ||
      (asset.dimensions
        ? asset.dimensions.width / asset.dimensions?.height
        : undefined),
  };

  return (
    <picture style={derivedStyles}>
      <source
        media="(max-width: 300px)"
        srcSet={asset ? `${sources.x1} 1x` : undefined}
      />

      <source
        media="(min-width: 500px)"
        srcSet={asset ? `${sources.x2} 2x` : undefined}
      />

      <source
        media="(min-width: 700px)"
        srcSet={asset ? `${sources.x3} 3x` : undefined}
      />

      <source
        media="(min-width: 1000px)"
        srcSet={asset ? `${sources.x3} 4x` : undefined}
      />

      <img
        src={sources.x3}
        alt={asset.altText}
        className={className}
        loading={config?.loading ?? 'lazy'}
        style={{ ...style, ...derivedStyles, objectFit: config?.resizeMode }}
        {...otherProps}
      />
    </picture>
  );
};

export { ImageAsset };
